<script setup lang="tsx">
import type { RiskConfig, RiskOverviewCriticalMethod } from '@/models/api-generated'
import CardRow from './CardRow.vue'
import RiskScoreBadge from '../RiskScoreBadge.vue'
import { getMethodStatusText, getResponsibleIcon, getStatusIcon } from '@/services/utils'
import { truncate } from '@/services/utils'
import { h } from 'vue'
import RisksTooltip from '../RisksTooltip.vue'

const props = defineProps<{
	config?: RiskConfig
}>()

const model = defineModel<RiskOverviewCriticalMethod[]>({ required: true })

function goToMethods(method: string) {
	const params = new URLSearchParams({
		reference: method,
	})
	window.location.href = `/app/methods?${params}`
}

const emit = defineEmits<{
	(e: 'go-to-risks', riskNames: string[], treatments: string[]): void
}>()

function goToRisks(names: string[]) {
	if (!names) return
	emit('go-to-risks', names, [])
}
</script>

<template>
	<div class="rounded-container p-4 overflow-auto text-nowrap">
		<p class="text-center fw-bold">
			{{ $t('risk.overview.criticalMethods') }}
		</p>

		<table class="w-100">
			<CardRow :underline="true">
				<template #col0>&nbsp;</template>
				<template #col1>{{ $t('method.status') }}</template>
				<template #col2>{{ $t('risk.risk', 1) }}</template>
				<template #col3>{{ $t('risk.residualRiskScore') }}</template>
				<template #col_end> {{ $t('risk.responsible') }} </template>
			</CardRow>

			<CardRow v-for="a in model" :key="a.name">
				<template #col0>
					<span v-one-tooltip="a.name" @click="goToMethods(a.name)" class="risks-cell">{{ truncate(a.name, 10) }}</span>
				</template>
				<template #col1>
					<span v-one-tooltip="getMethodStatusText(a.status)">
						<i :class="getStatusIcon(a.status, false).iconClass"></i>
					</span>
				</template>
				<template #col2>
					<div
						class="text-center risks-cell"
						v-one-tooltip="{
							content: () => h(RisksTooltip, { risks: a.risks, config: props.config }),
						}"
						@click="goToRisks(a.risks.map((r) => r.name))"
					>
						{{ a.risks.length }}
					</div></template
				>
				<template #col3> <RiskScoreBadge v-model="a.residual" :config="props.config" /></template>
				<template #col_end>
					<span class="icon-container">
						<i v-if="a.responsible" :class="getResponsibleIcon(a.responsible)"></i>
						{{ a.responsible ? a.responsible.label : '-' }}
					</span>
				</template>
			</CardRow>
		</table>
	</div>
</template>
